<template>
  <div class="wiki-create-page">
    <CommonOverlay :show="isUploadingImage" title="Uploading Image" message="Please wait while the image is uploaded..." />
    <!-- Header with breadcrumb and actions -->
    <div class="wiki-create-header">
      <div class="header-left">
        <router-link :to="{ path: `/app/wiki/`}" class="back-link">
          <i class="fa fa-arrow-left"></i> Back to Wiki
        </router-link>
        <h1 class="page-title">Create New Page</h1>
      </div>
      <div class="header-actions">
        <!-- Save button is in the CustomEditor component -->
      </div>
    </div>

    <!-- Main content wrapper -->
    <div class="wiki-create-content-wrapper">
      <div class="wiki-create-main">
        <ErrorDisplay/>

        <!-- Wiki Name Row - Full width, required -->
        <div class="wiki-name-row mt-3 mb-2">
          <div class="settings-panel wiki-name-panel" :class="{ 'wiki-name-error': wikiNameError }">
            <div class="panel-header">
              <span class="panel-icon">📝</span>
              <span class="panel-title">Wiki Name <span class="required-star">*</span></span>
            </div>
            <div class="panel-content">
              <b-form-input
                v-model="wikiName"
                placeholder="Enter wiki page name..."
                size="sm"
                class="wiki-name-input"
                :class="{ 'is-invalid': wikiNameError }"
                @input="wikiNameError = false"
              ></b-form-input>
              <small v-if="wikiNameError" class="text-danger mt-1 d-block">
                Wiki name is required.
              </small>
            </div>
          </div>
        </div>

        <!-- Settings Row - Side by Side Panels (matching WikiEdit) -->
        <div class="settings-row mb-3">
          <!-- Tags Panel -->
          <div class="settings-panel tags-panel">
            <div class="panel-header">
              <span class="panel-icon">🏷️</span>
              <span class="panel-title">Tags</span>
            </div>
            <div class="panel-content tags-content">
              <multiselect
                v-if="teamioTags"
                v-model="selectedTags"
                :options="teamioTags"
                track-by="Tag"
                :multiple="true"
                :taggable="true"
                tag-placeholder="Add this as new tag"
                placeholder="Search or add tags"
                @tag="addTag"
                label="Tag"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="false"
                :preselect-first="false"
                :hide-selected="true"
              ></multiselect>
            </div>
          </div>

          <!-- Page Settings Panel -->
          <div class="settings-panel page-settings-panel">
            <div class="panel-header">
              <span class="panel-icon">⚙️</span>
              <span class="panel-title">Page Settings</span>
            </div>
            <div class="panel-content">
              <b-form-group class="abc-checkbox compact-checkbox mb-1">
                <input type="checkbox" id="mark-as-email-template" class="custom-control-input" v-model="isEmailTemplate" @click="updateTemplateTag('email')" />
                <label for="mark-as-email-template"></label>Email Template
              </b-form-group>
              <b-form-group class="abc-checkbox compact-checkbox mb-0">
                <input type="checkbox" id="mark-as-sms-template" class="custom-control-input" v-model="isSmsTemplate" @click="updateTemplateTag('sms')" />
                <label for="mark-as-sms-template"></label>SMS Template
              </b-form-group>
            </div>
          </div>

          <!-- Email Subject Panel - Only show for Email templates -->
          <div v-if="isEmailTemplate" class="settings-panel email-subject-panel" :class="{ 'email-subject-error': emailSubjectError }">
            <div class="panel-header">
              <span class="panel-icon">✉️</span>
              <span class="panel-title">Email Subject <span class="required-star">*</span></span>
            </div>
            <div class="panel-content">
              <b-form-input
                v-model="emailSubject"
                placeholder="Enter email subject line..."
                size="sm"
                class="email-subject-input"
                :class="{ 'is-invalid': emailSubjectError }"
                @input="emailSubjectError = false"
              ></b-form-input>
              <small v-if="emailSubjectError" class="text-danger mt-1 d-block">
                Email subject is required for email templates.
              </small>
            </div>
          </div>

          <!-- Public Access Panel -->
          <div class="settings-panel public-access-panel">
            <div class="panel-header">
              <span class="panel-icon">🔗</span>
              <span class="panel-title">Public Access</span>
            </div>
            <div class="panel-content">
              <b-form-group class="abc-checkbox compact-checkbox mb-0">
                <input type="checkbox" id="mark-as-public" class="custom-control-input" v-model="isPublic" />
                <label for="mark-as-public"></label>Make publicly accessible
              </b-form-group>
            </div>
          </div>

          <!-- Translations Panel - Only show for SMS/Email templates -->
          <div v-if="isSmsTemplate || isEmailTemplate" class="settings-panel translation-panel">
            <div class="panel-header">
              <span class="panel-icon">🌐</span>
              <span class="panel-title">Translations</span>
            </div>
            <div class="panel-content">
              <div v-for="lang in translationLanguages" :key="lang.code" class="translation-lang-row">
                <input type="checkbox"
                       :id="'lang-' + lang.code"
                       v-model="selectedTranslationLanguages"
                       :value="lang.code"
                       class="translation-checkbox" />
                <label :for="'lang-' + lang.code" class="translation-label">
                  <span class="lang-flag">{{ lang.flag }}</span>
                  <span class="lang-name">{{ lang.name }}</span>
                </label>
              </div>
              <p class="auto-translate-info mb-0 mt-2">
                <small class="text-muted">Selected languages auto-translated on save</small>
              </p>
            </div>
          </div>



        </div>

              <!-- Page Content Section -->
              <div class="content-section mt-3">
                <div class="content-section-header">
                  <i class="fa fa-pencil-square-o content-section-icon"></i>
                  <span class="content-section-title">Page Content</span>
                  <span class="content-section-hint">Edit your page content below</span>
                  <b-dropdown variant="outline-secondary" size="sm" class="ml-2 insert-dropdown" boundary="viewport" no-caret>
                    <template slot="button-content">
                      <strong>+ Insert</strong> <i class="fa fa-caret-down ml-1"></i>
                    </template>
                    <div class="insert-dropdown-content">
                      <h6 class="insert-section-title">Variables</h6>
                      <div class="variable-grid px-2 pb-2">
                        <span v-for="v in availableVariables" :key="v.value"
                          class="variable-chip" :title="v.value" @click="insertVariable(v.value)">{{ v.label || v.value }}</span>
                      </div>
                      <template v-if="isReviewRequestTemplate">
                        <h6 class="insert-section-title border-top pt-2 mt-1">Reviews</h6>
                        <div class="variable-grid px-2 pb-2">
                          <span v-for="v in reviewVariables" :key="v.value"
                            class="variable-chip" :title="v.value" @click="insertVariable(v.value)">{{ v.label || v.value }}</span>
                        </div>
                      </template>
                      <h6 class="insert-section-title border-top pt-2 mt-1">Emojis</h6>
                      <div class="emoji-inline-grid px-2 pb-2">
                        <div v-for="(emojiGroup, category) in emojiTable" :key="category">
                          <h6 class="emoji-category-title">{{ category }}</h6>
                          <div class="emoji-grid">
                            <span v-for="(emoji, emojiName) in emojiGroup" :key="emojiName"
                              class="emoji-item" :title="emojiName"
                              @click="insertEmoji(emoji)">{{ emoji }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-dropdown>
                </div>
                <div class="content-editor-container">
                  <b-overlay :show="show" rounded="sm">
                    <CustomEditor
                      ref="customEditor"
                      v-if="editorReady"
                      :editorData="initData"
                      :editorTools="editorTools"
                      @save="save"
                      @onChange="onChange"
                    ></CustomEditor>
                    <div v-else style="min-height: 300px; display: flex; align-items: center; justify-content: center; color: #666;">
                      Loading editor...
                    </div>
                  </b-overlay>
                </div>
              </div>

              <!-- SMS status card: the single counter + warn/block guidance, in a clean readable
                   card (never on the preview panel's gradient). Shown for SMS templates whenever
                   there's content; template-aware ({{merge_tags}} rendered with sample values). The
                   save-gate enforces the hard block. -->
              <div v-if="isSmsTemplate && smsPlainText && smsPlainText.trim()" class="sms-status-card mt-4">
                <SmsGuidance :text="smsPlainText || ''" substitute-tags :show-balance="false" />
              </div>

              <!-- Inline SMS Preview Section — only for SMS templates AND only when the message is
                   clean (severity 'ok'). A long / over-limit message hides the phone mockup so the
                   status card above is the single, unambiguous signal — a polished preview of an
                   undeliverable text would mislead (chosen UX, 2026-07-10). -->
              <div v-if="isSmsTemplate && smsPreviewSeverity === 'ok'" class="sms-preview-section mt-4">
                <div class="preview-header" @click="togglePreviewSection">
                  <div class="preview-header-content">
                    <div class="preview-title">
                      <i class="fa" :class="showInlinePreview ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                      <span class="title-icon">📱</span>
                      <span class="title-text">SMS Preview</span>
                    </div>
                    <div class="preview-status">
                      <span class="status-badge status-pending">
                        <i class="fa fa-language"></i> Translations after save
                      </span>
                    </div>
                  </div>
                </div>

                <b-collapse v-model="showInlinePreview">
                  <!-- Phone Previews -->
                  <div class="inline-phone-preview single-phone">
                    <!-- English Phone -->
                    <div class="phone-mockup-inline">
                      <div class="phone-frame-inline">
                        <div class="phone-notch-inline"></div>
                        <div class="phone-screen-inline">
                          <div class="phone-header-inline">
                            <span class="phone-time-inline">{{ currentTime }}</span>
                            <span class="phone-carrier-inline">TeamIO</span>
                          </div>
                          <div class="phone-content-inline">
                            <!-- Show split messages if applicable -->
                            <template v-if="smsSplitPreview && smsSplitPreview.length > 1">
                              <div
                                v-for="(segment, idx) in smsSplitPreview"
                                :key="'seg-'+idx"
                                class="message-bubble-inline outgoing"
                                :class="{'split-bubble': true}"
                              >
                                <div class="split-indicator">{{ idx + 1 }}/{{ smsSplitPreview.length }}</div>
                                <div class="message-text-inline">{{ segment }}</div>
                                <div class="message-time-inline">{{ idx === 0 ? 'Just now' : '' }}</div>
                              </div>
                            </template>
                            <!-- Single message -->
                            <template v-else>
                              <div class="message-bubble-inline outgoing">
                                <div class="message-text-inline" v-html="englishSmsPreview"></div>
                                <div class="message-time-inline">Just now</div>
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                      <div class="phone-label-inline">🇺🇸 English</div>
                    </div>
                    <!-- Translation phones will appear after first save -->
                  </div>
                  
                  <!-- Info Note -->
                  <div class="preview-footer">
                    <i class="fa fa-info-circle"></i>
                    Variables like <code v-pre>{{patient_first_name}}</code> will be replaced with actual patient data when sent.
                  </div>
                </b-collapse>
              </div>

              <!-- Inline Email Preview Section - Only for Email templates -->
              <div v-if="isEmailTemplate" class="email-preview-section mt-4">
                <div class="email-preview-header" @click="showEmailPreview = !showEmailPreview">
                  <div class="preview-header-content">
                    <div class="preview-title">
                      <i class="fa" :class="showEmailPreview ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                      <span class="title-icon">📧</span>
                      <span class="title-text">Email Preview</span>
                    </div>
                    <div class="preview-status">
                      <span class="status-badge status-email">
                        <i class="fa fa-envelope"></i> Email Template
                      </span>
                    </div>
                  </div>
                </div>
                
                <b-collapse v-model="showEmailPreview">
                  <div class="email-mockup">
                    <!-- Email Client Header -->
                    <div class="email-client-header">
                      <div class="email-dots">
                        <span class="dot red"></span>
                        <span class="dot yellow"></span>
                        <span class="dot green"></span>
                      </div>
                      <div class="email-tab">
                        <i class="fa fa-envelope-o"></i> New Message
                      </div>
                    </div>
                    
                    <!-- Email Content -->
                    <div class="email-content">
                      <div class="email-field">
                        <span class="field-label">From:</span>
                        <span class="field-value">{{ siteName }} &lt;{{ siteVerifiedEmail }}&gt;</span>
                      </div>
                      <div class="email-field">
                        <span class="field-label">To:</span>
                        <span class="field-value text-muted">John Smith &lt;patient@example.com&gt;</span>
                      </div>
                      <div class="email-field">
                        <span class="field-label">Subject:</span>
                        <span class="field-value subject" v-html="emailSubjectPreview"></span>
                      </div>
                      <div class="email-divider"></div>
                      <div class="email-body" v-html="emailBodyPreview"></div>
                    </div>
                  </div>
                  
                  <div class="email-preview-footer">
                    <i class="fa fa-info-circle"></i>
                    Variables will be replaced with actual patient data. Email styling may vary by email client.
                  </div>
                </b-collapse>
              </div>

      </div>

      <!-- Sidebar with browse by tag -->
      <div class="wiki-create-sidebar" v-if="siteTags && siteTags.length > 0">
        <div class="sidebar-section">
          <h4 class="sidebar-title">Browse by Tag</h4>
          <div class="sidebar-tags">
            <router-link
              v-for="(tag, index) in siteTags"
              :key="index"
              :to="{ path: `/app/wiki/${tag.Tag}` }"
              class="sidebar-tag"
            >
              {{ tag.Tag }}
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <b-modal ref="edit" id="edit" title="Edit Access" body-class="bg-white">
      <h5 class="mb-4">
        Page:
        <strong class="ml-2">Page Title</strong>
      </h5>
      <b-form-group class="abc-checkbox">
        <b-form-checkbox v-model="allEmployeesCanSee" unchecked-value="false">
            All team members can view this page
        </b-form-checkbox>
      </b-form-group>

      <p class="add-edit-text">Add Edit access:</p>
      <p>The followings team members can change/Edit the page</p>
      
      <b-form class="routine-form">
        <multiselect
          v-if="this.employees"
          v-model="employeesWhoCanEdit"
          :options="this.employees"
          :multiple="true"
          :taggable="false"
          label="Name"
          track-by="Name" 
          aria-placeholder="Select a Team Member"
          >
              <template slot="tag" slot-scope="props">
              <span class="custom__tag">
                <img class="option__image" src='../../assets/icons-user-40.png' alt="Team member image"/>
                <span>{{ props.option.Name }}</span>
                <i tabindex="1" class="multiselect__tag-icon" @click="removedMember(props.option)"></i>
                <!-- <span class="custom__remove" @click="removedMember(props.option)">X</span> -->
              </span>
            </template>
            <template slot="option" slot-scope="props">
            <img class="option__image" src='../../assets/icons-user-40.png' alt="Team member image" />
            <div class="option__desc ">
              <span class="option__title">{{ props.option.Name }}</span>
            </div>
          </template>
        </multiselect>
      </b-form>
      <div slot="modal-footer" class="w-100">
        <!-- <b-button variant="danger" size="sm" class="float-right" @click="hideModal">Cancel</b-button> -->
        <b-button variant="success" size="sm" class="float-right mr-3" @click="hideModal">Save</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */

import Multiselect from "vue-multiselect";
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import CustomEditor from "@/components/Editor/Editor.vue";
import CommonOverlay from "@/components/CommonOverlay.vue";
import SmsGuidance from "@/components/SmsGuidance.vue";
import { getSmsGuidance } from "@/utils/smsGuidance";
import { findUnknownVariables, describeUnknownVariables, isReviewRequestTagged, REVIEW_SAMPLE_VARS } from "@/utils/templateVariables";
import { extractSmsTextFromBlocks } from "@/utils/smsTemplateText";
import axios from 'axios';
import { uploadToS3 } from "@/utils/fileUpload";
import _ from 'lodash';
import { mapState, mapActions, mapMutations } from 'vuex';
import CustomLink from "@/components/Editor/custom-link/index.js";
import CustomExternalLink from "@/components/Editor/custom-external-link/index.js";
import moment from 'moment-timezone';
import { pickVerifiedFromEmail } from '@/utils/verifiedSender.js';

// Ensure environment-aware API routing for component-level axios usage
axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

const EMOJI_TABLE = {
    'Frequently Used': { thumbs_up: '👍', heart: '❤️', smile: '😄', wave: '👋', pray: '🙏', party: '🎉', star: '⭐', check: '✅', fire: '🔥', clap: '👏' },
    'Smileys': { grinning: '😀', smiley: '😃', smile: '😄', grin: '😁', laughing: '😆', blush: '😊', wink: '😉', heart_eyes: '😍', kissing_heart: '😘', thinking: '🤔', neutral: '😐', relieved: '😌', sunglasses: '😎', cry: '😢', sob: '😭', joy: '😂' },
    'Gestures': { thumbs_up: '👍', thumbs_down: '👎', ok_hand: '👌', wave: '👋', clap: '👏', raised_hands: '🙌', pray: '🙏', muscle: '💪', point_right: '👉', point_left: '👈' },
    'Celebrations': { party: '🎉', confetti: '🎊', balloon: '🎈', gift: '🎁', cake: '🎂', trophy: '🏆', sparkles: '✨', star: '⭐', tada: '🎊', ribbon: '🎀' },
    'Hearts & Symbols': { red_heart: '❤️', orange_heart: '🧡', yellow_heart: '💛', green_heart: '💚', blue_heart: '💙', purple_heart: '💜', check: '✅', x: '❌', exclamation: '❗', question: '❓' }
};

export default {
  name: "WikiCreate",
  components: { Multiselect, CustomEditor, ErrorDisplay, CommonOverlay, SmsGuidance },
  props: ['pages'],

  /*
  async beforeRouteEnter (to, from, next) {
    console.log('in before route enter');
    
    try {
      
      console.log(`sitePages: ${JSON.stringify(sitePages)}`);
      next(vm => {
        vm.sitePages = sitePages;
      })
    } catch (err) {
      next(false)
    }
    
  },
  */

  async created() {    
  },

  async mounted() {
    await this.init();
    let self = this;
    if (self.existingLinks) {
      this.sitePages[0].forEach(function(p) {
        self.existingLinks.push({
          name: p.Name,
          url: `/app/wikiview/${p.WikiPageID}`,
        });
      });
      window.localStorage.existingLinks = JSON.stringify(this.existingLinks);
    }

    // Track cursor position for + Insert variable/emoji insertion
    this._selectionHandler = () => this.saveEditorSelection();
    document.addEventListener('selectionchange', this._selectionHandler);
  },

  data() {
    const vm = this; // capture Vue instance for upload overlay toggle
    return {
      show: false,
      saved: false,
      isUploadingImage: false,
      wikiName: '',
      wikiNameError: false,
      isEmailTemplate: false,
      isSmsTemplate: false,
      emailSubject: '',  // Custom email subject line for email templates
      emailSubjectError: false,
      isPublic: false,  // Public accessibility flag
      translationLanguages: [
        { code: 'spa', name: 'Spanish', flag: '\u{1F1EA}\u{1F1F8}', awsCode: 'es' },
        { code: 'rus', name: 'Russian', flag: '\u{1F1F7}\u{1F1FA}', awsCode: 'ru' }
      ],
      selectedTranslationLanguages: [],  // User-selected language codes, default none
      wikiPageID: null,
      sendgridTemplateID: null,      
      sitePages: [],
      packets: [],  // Form Packets for this site, fetched alongside getWikiPages. Used by CustomLink picker + paste-handler lookup.
      siteTags: [],
      teamioTags: [],
      selectedTags: [],
      options: ['a', 'b'],
      employeesWhoCanEdit: '',
      allEmployeesCanSee: false,
      editorReady: false,
      initData: {
        time: new Date().getTime(),
        blocks: [
          {
            type: "header",
            data: { text: "Start writing...", level: 1 }
          }
        ],
        version: "2.26.4"
      },
      editorTools: {
        header: {
          config: {
            placeholder: 'Enter a header',
            levels: [1, 2, 3, 4],
            defaultLevel: 1
          },
          inlineToolbar: ["marker", "italic", "bold", "link", "wikiLink"]
        },
        paragraph: {
          inlineToolbar: true,
          config: {
            placeholder: 'Enter your text here...'
          }
        },
        simpleImage: {
          placeholder: 'Paste image URL here...',
          inlineToolbar: true
        },
        // Use official @editorjs/image with S3 upload configuration (matched with WikiEdit.vue)
        image: {
          config: {
            uploader: {
              async uploadByFile(file) {
                vm.isUploadingImage = true;
                try {
                  const result = await uploadToS3({
                    file,
                    siteID: window.localStorage.teamioSiteID,
                    teamioType: 'WIKI'
                  });

                  return {
                    success: 1,
                    file: {
                      url: result.url,
                      name: result.name,
                      size: result.size
                    }
                  };
                } catch (error) {
                  console.error('S3 upload error:', error);
                  throw error;
                } finally {
                  vm.isUploadingImage = false;
                }
              },
              async uploadByUrl(url) {
                // Reject non-HTTP URLs
                if (!url || !url.match(/^https?:\/\/.+/i)) {
                  return { success: 0, message: 'Invalid image URL' };
                }

                vm.isUploadingImage = true;
                try {
                  // Download the external image and re-upload to our S3
                  // This prevents data loss if the external URL expires (e.g. Scribe signed URLs)
                  const response = await fetch(url);
                  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);

                  const blob = await response.blob();

                  // Extract a clean filename from the URL path
                  let fileName;
                  try {
                    const urlPath = new URL(url).pathname;
                    fileName = decodeURIComponent(urlPath.split('/').pop() || '');
                  } catch (e) {
                    fileName = '';
                  }
                  if (!fileName || fileName === '/' || !fileName.includes('.')) {
                    fileName = `pasted-image-${Date.now()}.png`;
                  }

                  const file = new File([blob], fileName, { type: blob.type || 'image/png' });

                  const result = await uploadToS3({
                    file,
                    siteID: window.localStorage.teamioSiteID,
                    teamioType: 'WIKI'
                  });

                  return {
                    success: 1,
                    file: {
                      url: result.url,
                      name: result.name,
                      size: result.size
                    }
                  };
                } catch (error) {
                  console.warn('uploadByUrl: Could not re-upload to S3, falling back to external URL.', error);
                  // Fallback: preserve the external URL rather than losing the image entirely
                  return { success: 1, file: { url: url } };
                } finally {
                  vm.isUploadingImage = false;
                }
              }
            }
          }
        },
        embed: {
          inlineToolbar: true,
          config: {
            services: {
              youtube: {
                // Custom regex to handle YouTube URLs with extra query parameters
                regex: /(?:https?:\/\/)?(?:www\.)?(?:(?:youtu\.be\/)|(?:youtube\.com)\/(?:v\/|u\/\w\/|embed\/|watch))(?:(?:\?v=)?([^#&?=\s]+))(?:[?&][^#\s]*)?/,
                embedUrl: 'https://www.youtube.com/embed/<%= remote_id %>',
                html: '<iframe style="width:100%;" height="320" frameborder="0" allowfullscreen></iframe>',
                height: 320,
                width: 600
              },
              vimeo: true,
              coub: true,
              codepen: true,
              instagram: true,
              twitter: true,
              gfycat: true,
              imgur: true,
              pinterest: true,
              github: true
            }
          }
        },
        list: {
          inlineToolbar: true
        },
        delimiter: {},
        table: {
          inlineToolbar: true
        },
        marker: {
          shortcut: "CMD+SHIFT+M"
        },
        inlineCode: {
          shortcut: "CMD+SHIFT+C"
        },
        checklist: {
          inlineToolbar: true
        },       
        wikiLink: {
          class: CustomLink,
          inlineToolbar: true
        },
        // Override built-in link tool with wider input + OK/Cancel buttons
        link: {
          class: CustomExternalLink
        }
      },
      value: [],
      simpleDropdownVariant: "Select",
      isPickerActive: false,
      existingLinks: [],
      showInlinePreview: true,  // Open by default for SMS preview
      showEmailPreview: true,   // Open by default for email preview
      liveEditorContent: null,  // Stores live editor content for real-time preview
      previewUpdateTimeout: null,  // Debounce timer for preview updates
      availableVariables: [
        { value: '{{patient_first_name}}', primary: false },
        { value: '{{patient_last_name}}', primary: false },
        { value: '{{guarantor_first_name}}', primary: false },
        { value: '{{guarantor_last_name}}', primary: false },
        { value: '{{apt_date}}', primary: false },
        { value: '{{apt_dayofweek}}', primary: false },
        { value: '{{family_appts}}', primary: false },
        { value: '{{confirm_button}}', primary: false },
        { value: '{{site_name}}', primary: false },
        { value: '{{site_address1}}', primary: false },
        { value: '{{site_address2}}', primary: false },
        { value: '{{site_city}}', primary: false },
        { value: '{{site_state}}', primary: false },
        { value: '{{site_zip}}', primary: false },
        { value: '{{site_officephone}}', primary: false },
        // Email Signature — the office's signature block (logo, name, phone, address,
        // website, socials) configured under Settings -> Email. EMAIL ONLY: it resolves to
        // an HTML block, so wiki-service normalizes it to triple-brace at bake time and the
        // SMS paths strip it. Keep in sync with WikiEdit.vue, WikiImportModal.vue,
        // utils/templateVariables.js and wiki-service TEAMIO_VARIABLES.
        { value: '{{email_signature_block}}', label: '{{email_signature_block}} (Email Signature)', primary: false },
        // {{employee_name}} is intentionally absent: it still RESOLVES (permanent legacy
        // alias) but offering it alongside {{employee_full_name}} would show two chips that
        // do the same thing. Keep in sync with wiki-service TEAMIO_VARIABLES.
        { value: '{{employee_full_name}}', primary: false },
        { value: '{{employee_first_name}}', primary: false },
        { value: '{{payment_link}}', primary: true },
        { value: '{{patient_balance}}', primary: true },
        { value: '{{total_amount}}', primary: true },
        { value: '{{recall_due_date}}', primary: false },
        { value: '{{online_apttype}}', primary: false },
        { value: '{{apt_provider}}', primary: false }
      ],
      // Shown only on pages tagged review-request: only the Reviews send path fills it
      // (utils/templateVariables REVIEW_TEMPLATE_VARIABLES). Keep in sync with WikiEdit.vue.
      reviewVariables: [
        { value: '{{review_link}}', primary: true }
      ],
      emojiTable: EMOJI_TABLE,
      savedRange: null,
      savedBlockIndex: null,
    };
  },

  beforeDestroy() {
    if (this._selectionHandler) {
      document.removeEventListener('selectionchange', this._selectionHandler);
    }
  },

  watch: {
  },

  methods: {
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
    ...mapActions('customer', ['getSites', 'getEmployees']),
    ...mapActions('wiki', ['createWiki', 'updateWiki', 'getSiteTags', 'getWikiPages', 'getWikiTranslation']),
    ...mapActions('forms', ['getPackets']),

    /**
     * Save the current selection/cursor position inside the EditorJS editor.
     * Called on every selectionchange event so we always have the last known
     * cursor position, even after the editor loses focus to the dropdown.
     */
    saveEditorSelection() {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      const editorEl = this.$refs.customEditor?.$el;
      if (!editorEl) return;

      // Only save if the selection is inside the editor
      if (editorEl.contains(sel.anchorNode)) {
        this.savedRange = sel.getRangeAt(0).cloneRange();
        const editor = this.$refs.customEditor?.editor;
        if (editor) {
          this.savedBlockIndex = editor.blocks.getCurrentBlockIndex();
        }
      }
    },

    /**
     * Insert a variable at the current cursor position in the editor
     * @param {string} variable - The variable text to insert (e.g., '{{patient_first_name}}')
     */
    async insertVariable(variable) {
      try {
        const editor = this.$refs.customEditor?.editor;
        if (!editor) {
          console.warn('Editor not ready');
          return;
        }

        // Use saved range if available (cursor position before dropdown stole focus)
        if (this.savedRange) {
          const range = this.savedRange;

          // Find the contenteditable element that contains this range
          const blockIndex = this.savedBlockIndex >= 0 ? this.savedBlockIndex : editor.blocks.getCurrentBlockIndex();
          if (blockIndex >= 0) {
            const block = editor.blocks.getBlockByIndex(blockIndex);
            if (block) {
              const editableElement = block.holder.querySelector('[contenteditable="true"]');
              if (editableElement) {
                // Focus the element first so EditorJS tracks the block
                editableElement.focus();

                // Restore the saved range
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);

                // Insert at cursor position
                range.deleteContents();
                const textNode = document.createTextNode(variable);
                range.insertNode(textNode);

                // Move cursor after inserted text
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                sel.removeAllRanges();
                sel.addRange(range);

                // Save updated position
                this.savedRange = range.cloneRange();

                // Trigger change event
                editableElement.dispatchEvent(new Event('input', { bubbles: true }));
                return;
              }
            }
          }
        }

        // Fallback: no saved range — insert a new paragraph block
        await editor.blocks.insert('paragraph', { text: variable });
      } catch (error) {
        console.error('Error inserting variable:', error);
      }
    },

    async insertEmoji(emoji) {
      await this.insertVariable(emoji);
    },

    async init() {
      if(!this.pages) {
        console.log('pulling pages from DB');
        this.sitePages = await this.getWikiPages({siteID: this.siteID});
        this.siteTags = this.sitePages[1];
        this.teamioTags = this.sitePages[2];
      }
      else {
        console.log('reusing passed in pages');
        this.sitePages = this.pages;
        this.siteTags = this.pages[1];
        this.teamioTags = this.pages[2];
      }

      // Fetch Form Packets separately so they can appear in the WikiLink picker alongside forms.
      // Keep sitePages[3] (forms-only) untouched — other code paths in this file depend on that shape.
      try {
        const packetsRes = await this.getPackets({ siteID: this.siteID });
        this.packets = Array.isArray(packetsRes) ? packetsRes : [];
      } catch (err) {
        console.error('Failed to load packets for WikiLink picker:', err);
        this.packets = [];
      }

      // Build the merged list for the picker. Forms keep their original shape; packets are flagged
      // via `_isPacket: true` so CustomLink can render the PACKET badge and insert the correct template var.
      const formsSlot = this.sitePages && this.sitePages[3] ? this.sitePages[3] : [];
      const formsForLink = [
        ...formsSlot.map(f => ({ ...f, _isPacket: false })),
        ...this.packets.map(p => ({ Name: p.Name, PacketID: p.PacketID, _isPacket: true })),
      ];

      // Set up wikiLink config after data is loaded. Pass `packets` separately so
      // findReadableNameFromHref can resolve {{patient_link_for_packet_UUID}} on edit.
      this.editorTools.wikiLink.config = {
        wikiPages: this.sitePages[0],
        forms: formsForLink,
        packets: this.packets,
        mode: process.env.VUE_APP_NODE_ENV
      };
      
      // Debug logging to verify CustomLink is working
      console.log('WikiCreate - CustomLink class:', CustomLink);
      console.log('WikiCreate - WikiLink config:', this.editorTools.wikiLink);
      console.log('WikiCreate - wikiPages count:', this.sitePages && this.sitePages[0] ? this.sitePages[0].length : 'undefined');
      console.log('WikiCreate - forms count:', this.sitePages && this.sitePages[3] ? this.sitePages[3].length : 'undefined');
      console.log('WikiCreate - sitePages structure:', this.sitePages);
      
      // Set editor ready flag to render the CustomEditor component
      this.editorReady = true;
      
      // Manually enhance existing links after editor loads
      setTimeout(() => {
        this.enhanceExistingLinks();
      }, 2000);
    },

    addTag(newTag) {
      // Normalize before matching: trim + lowercase. Prevents creating
      // duplicate rows in teamio.tags when the user re-types an existing
      // tag name with different casing or whitespace.
      const norm = String(newTag || '').trim().toLowerCase();
      if (!norm) return;
      const tagExistsInTeamio = this.teamioTags.find(
        t => String(t.Tag || '').trim().toLowerCase() === norm
      );

      const tag = {
        Tag: tagExistsInTeamio ? tagExistsInTeamio.Tag : String(newTag).trim(),
        TagID: tagExistsInTeamio ? tagExistsInTeamio.TagID : ''
      };

      const existsIndex = this.selectedTags.findIndex(
        t => String(t.Tag || '').trim().toLowerCase() === norm
      );
      if(existsIndex === -1)
        this.selectedTags.push(tag);
    },

    removeTag(tagName) {
      let index1 = this.selectedTags.findIndex(t => t.Tag === tagName);      
      if(index1 !== -1)
        this.selectedTags.splice(index1,1);
    },

    updateTemplateTag(type) {
      if(type === "email") {
        if(!this.isEmailTemplate)
          this.addTag('email-template');
        else {          
          this.removeTag('email-template');
        }
      } else if(type === "sms") {
        if(!this.isSmsTemplate)
          this.addTag('sms-template');
        else {         
          this.removeTag('sms-template');
        }
      }
    },

    stripHtml(html)
    {
      var tmp = document.createElement("DIV");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    },

    stripHtmlForPreview(html) {
      // For phone preview, keep some formatting but strip complex HTML
      if (!html) return '';
      // Replace <br> with actual line breaks for display
      let text = html.replace(/<br\s*\/?>/gi, '<br>');
      // Keep links visible but simplified
      text = text.replace(/<a[^>]*>([^<]*)<\/a>/gi, '$1');
      // Strip other HTML tags
      const tmp = document.createElement("DIV");
      tmp.innerHTML = text;
      return tmp.innerHTML;
    },

    // Plain SMS text from the editor data being saved — reads the definitive `e` passed to
    // save() (not the live computed) so the save-gate measures exactly what's persisted.
    // Delegates to the shared FE/BE twin so this agrees with the backend renderer: header
    // blocks are dropped as titles UNLESS they're all the template has, in which case they
    // are the message (see src/utils/smsTemplateText.js — PROD outage 2026-08-04).
    extractSmsTextFromEditorData(data) {
      if (!data) return '';
      return extractSmsTextFromBlocks(data.blocks).trim();
    },

    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    },

    replaceAll(str, find, replace) {
      return str.replace(new RegExp(this.escapeRegExp(find), 'g'), replace);
    },

    showModal() {
        this.$refs['edit'].show()
    },

    hideModal() {
      this.$refs['edit'].hide()
    },

    async save(e) {
      // Validate wiki name is filled out
      if (!this.wikiName || this.wikiName.trim() === '') {
        this.wikiNameError = true;
        this.$toast.error("Please enter a wiki name before saving.");
        return;
      }

      // Validate email subject if email template is selected
      if (this.isEmailTemplate && (!this.emailSubject || this.emailSubject.trim() === '')) {
        this.emailSubjectError = true;
        this.$toast.error("Please enter an email subject before saving.");
        return;
      }

      // SMS template save-gate (shared utils/smsGuidance — the PRIMARY defense against
      // sending over-limit texts). Hard-block a template whose sample render exceeds the
      // carrier segment cap; a costly / near-cap one only shows the inline warning banner
      // (real patient data is substituted at send time and can push a near-cap template
      // over, where the backend then blocks it and the patient silently misses it).
      // Unknown-variable gate — ANY message template, SMS or email.
      //
      // The Wiki is one authoring surface: the same + Insert palette serves both, and a page can
      // be flagged as both at once. Email is NOT a SendGrid-only world — utility.js:5128 runs
      // emailHtml through the very same replaceTemplateVariables, which substitutes only TRUTHY
      // values, so an unrecognised {{token}} reaches the recipient verbatim either way. The
      // pasted review-request pack proved it: "Review Request SMS" and "Review Request Email"
      // both carry {{first_name}} / {{practice_name}} / {{review_link}}.
      //
      // Deliberately NOT run on ordinary wiki pages — 2 live prod pages are documentation that
      // mentions "{{" in prose, and blocking those would be a gate on something that works.
      //
      // Detection runs over the SERIALISED editor payload, so a variable typed into a header or
      // list block is caught too — header-only templates are what the 2026-08-04 outage was.
      // The Reviews variables pass only on review-request pages (their one send path).
      if (this.isSmsTemplate || this.isEmailTemplate) {
        const unknownVars = findUnknownVariables(JSON.stringify(e), { tags: this.selectedTagNames });
        if (unknownVars.length) {
          this.$toast.error(describeUnknownVariables(unknownVars));
          return; // matches the sibling gates — `show` is not toggled until after them
        }
      }

      if (this.isSmsTemplate) {
        const smsText = this.extractSmsTextFromEditorData(e);
        // Empty-body gate. There was only ever a MAXIMUM length check here, so a template with
        // no text saved happily and then failed at send time — on 2026-08-04 that crashed the
        // send worker and stalled a practice's whole message queue. An SMS template with nothing
        // to say is never valid; block it at the source.
        if (!smsText || smsText.trim() === '') {
          this.$toast.error("This SMS template has no message text. Add the text you want texted to patients before saving.");
          return;
        }
        const g = getSmsGuidance(smsText, {
          substituteTags: true,
          vars: this.isReviewRequestTemplate ? REVIEW_SAMPLE_VARS : undefined,
        });
        if (g.blocked) {
          this.$toast.error(`${g.title}. ${g.message}`);
          return; // never save an over-carrier-limit SMS template
        }
        // A warn state (long / emoji / near the limit) is NOT a prompt — the <SmsGuidance>
        // banner under the editor already shows it while typing. Save just saves.
      }

      this.show = !this.show;
      let payload;
      console.log('WikiCreate save() - Editor data received:', JSON.stringify(e, null, 2));
      
      // Check for custom links in paragraphs
      e.blocks.forEach((block, index) => {
        if (block.type === 'paragraph' && block.data && block.data.text) {
          console.log(`Block ${index} (${block.type}) text:`, block.data.text);
          if (block.data.text.includes('custom-link')) {
            console.log('✅ Found custom-link in block:', index);
          }
        }
      });
      
      // Send all images in the content - backend will handle processing correctly
      const uploadedImages = _.filter(e.blocks, {"type": "image"})
        .map(function(object) {
          return _.get(object, 'data.file.url');
        })
        .filter(function(imageUrl) {
          // Include all valid image URLs - let backend determine what needs processing
          return imageUrl && imageUrl.trim() !== '';
        });

      // Log for debugging image persistence issues
      console.log('WikiCreate - Saving images:', uploadedImages);      

      // Clean up problematic &nbsp; sequences but preserve necessary spaces
      let allpara = e.blocks.filter(b => b.type === 'paragraph');          
      let _this = this;
      allpara.forEach(function(b) {             
        // Only remove multiple consecutive &nbsp; or ones at start/end
        // Preserve single &nbsp; that might be intentional spacing
        b.data.text = b.data.text
          .replace(/&nbsp;{2,}/g, ' ')  // Multiple &nbsp; -> single space
          .replace(/^&nbsp;|&nbsp;$/g, '') // Remove from start/end
          .replace(/&nbsp;(?=<)/g, ' ')  // Before HTML tags
          .replace(/(?=>)&nbsp;/g, ' '); // After HTML tags
      });      

      // CRITICAL: Clean enhanced links back to template syntax before saving
      console.log('=== CLEANING ENHANCED LINKS BEFORE SAVE (WIKICREATE) ===');
      this.cleanEnhancedLinks(e);

      if(this.saved) {
        
        if(!this.wikiPageID) {          
          this.$toast.error("Something did not work right. Please try reloading this page and try again");
          return;
        }

        payload = {
          WikiPageID: this.wikiPageID,
          IsEmailTemplate: this.isEmailTemplate,
          IsSmsTemplate: this.isSmsTemplate,
          IsPublic: this.isPublic,  // Public accessibility flag
          SendgridTemplateID: this.sendgridTemplateID,

          PageToUpdate : {
            Name: this.wikiName.trim(),
            Content: JSON.stringify(e),
            SiteID: this.siteID
          },
          Tags: this.selectedTags.length > 0? this.selectedTags : null,
          UploadedImages: uploadedImages,
          SiteID: this.siteID,
          employeesWhoCanEdit : this.employeesWhoCanEdit,
          AllEmployeesCanSee: this.allEmployeesCanSee === "false" ? 0 : 1,
          selectedLanguages: (this.isSmsTemplate || this.isEmailTemplate)
              ? this.selectedTranslationLanguages
              : []
        };
        console.log('payload to update Wiki:' + JSON.stringify(payload));
        try {
          await this.updateWiki(payload);
          this.show = !this.show;
          this.$toast.success("Wiki page updated successfully!");
        } catch (error) {
          this.show = !this.show;
          console.log('Error creating a wiki page:', err);
          this.$toast.error("Error updating wiki page. Please try again later");
        }
        
      } else {
          payload = {
            IsEmailTemplate: this.isEmailTemplate,
            IsSmsTemplate: this.isSmsTemplate,
            IsPublic: this.isPublic,  // Public accessibility flag
            EmailSubject: this.emailSubject,  // Custom email subject line for email templates
            PageToCreate : {
              Name: this.wikiName.trim(),
              Content: JSON.stringify(e),
              SiteID: this.siteID,
              CreatedByEmployeeID: this.userID,
              CreatedAt: moment().format('YYYY-MM-DDTHH:mm:ss')
            },
            Tags: this.selectedTags.length > 0? this.selectedTags : null,
            UploadedImages: uploadedImages,
            SiteID: this.siteID,
            employeesWhoCanEdit : this.employeesWhoCanEdit,
            AllEmployeesCanSee: this.allEmployeesCanSee === "false" ? 0 : 1,
            selectedLanguages: (this.isSmsTemplate || this.isEmailTemplate)
                ? this.selectedTranslationLanguages
                : []
          };

          console.log('payload to create wiki:' + JSON.stringify(payload));
          
          let res;

          try {
            res = await this.createWiki(payload);
            if(res && res.status === 200) {
              this.wikiPageID = res.data.WikiPageID;
              if(res.data.SendgridTemplateID)
                this.sendgridTemplateID = res.data.SendgridTemplateID;
              this.saved = true;
              
              // Handle translation results (show errors only, not success)
              if (res.data.translationResults) {
                for (const [langCode, result] of Object.entries(res.data.translationResults)) {
                  if (!result.success && result.error) {
                    this.$toast.warning(`${langCode} translation failed: ${result.error}`);
                  }
                }
              }
            }
            this.show = !this.show;
            this.$toast.success("Wiki page created successfully!");

          } catch (error) {
            this.show = !this.show;
            console.log('Error creating a wiki page:', error);
            this.$toast.error("Error creating wiki page. Please try again later");
          }
          
                  }
      },

      // Manual enhancement of existing links for WikiCreate page
      enhanceExistingLinks() {
        try {
          console.log('=== MANUALLY ENHANCING EXISTING LINKS IN WIKICREATE ===');
          
          // Find all existing links in the editor
          const editorElement = document.querySelector('.codex-editor__redactor');
          if (!editorElement) {
            console.log('Editor element not found in WikiCreate');
            return;
          }
          
          const links = editorElement.querySelectorAll('a.custom-link, a[href*="/app/wikiview/"], a[href*="{{patient_link_for_form_"]');
          console.log('Found existing links in WikiCreate:', links.length);
          
          // Helper function to find readable name from href
          const findReadableNameFromHref = (href) => {
            let targetId = null;
            
            // IMPORTANT: URL decode the href first to handle browser encoding
            const decodedHref = decodeURIComponent(href);
            console.log('🔍 WikiCreate - Original href:', href);
            console.log('🔍 WikiCreate - Decoded href:', decodedHref);
            
            // Handle wiki links: /app/wikiview/ID (handle both full URLs and relative paths)
            if (decodedHref.includes('/app/wikiview/')) {
              targetId = decodedHref.split('/app/wikiview/')[1];
              
              if (targetId && this.sitePages && this.sitePages[0]) {
                // Try multiple possible field names for matching
                let wikiPage = this.sitePages[0].find(page => page.guid === targetId);
                if (!wikiPage) {
                  wikiPage = this.sitePages[0].find(page => page.WikiPageID === targetId);
                }
                
                if (wikiPage) {
                  const name = wikiPage.name || wikiPage.Name || wikiPage.title || wikiPage.Title;
                  return name;
                }
              }
            }
            
            // Handle form template links: {{patient_link_for_form_ID}} (use decoded href)
            if (decodedHref.includes('{{patient_link_for_form_') && decodedHref.includes('}}')) {
              const match = decodedHref.match(/\{\{patient_link_for_form_(.+?)\}\}/);
              console.log('🔍 WikiCreate - Form template link detected (decoded):', decodedHref);
              console.log('🔍 WikiCreate - Regex match result:', match);
              if (match) {
                targetId = match[1];
                console.log('🔍 WikiCreate - Extracted form ID:', targetId);
                console.log('🔍 WikiCreate - Available forms count:', this.sitePages && this.sitePages[3] ? this.sitePages[3].length : 0);
                if (targetId && this.sitePages && this.sitePages[3]) {
                  // Debug: Show first few forms to understand structure
                  console.log('🔍 WikiCreate - First form structure:', this.sitePages[3][0]);
                  console.log('🔍 WikiCreate - First form keys:', this.sitePages[3][0] ? Object.keys(this.sitePages[3][0]) : 'none');
                  
                  // Try multiple possible field names for matching (including FormIDForWeb)
                  let form = this.sitePages[3].find(f => f.guid === targetId);
                  console.log('🔍 WikiCreate - Trying guid match:', form);
                  if (!form) {
                    form = this.sitePages[3].find(f => f.FormID === targetId);
                    console.log('🔍 WikiCreate - Trying FormID match:', form);
                  }
                  if (!form) {
                    form = this.sitePages[3].find(f => f.id === targetId);
                    console.log('🔍 WikiCreate - Trying id match:', form);
                  }
                  if (!form) {
                    form = this.sitePages[3].find(f => f.GUID === targetId);
                    console.log('🔍 WikiCreate - Trying GUID match:', form);
                  }
                  if (!form) {
                    form = this.sitePages[3].find(f => f.FormIDForWeb === targetId);
                    console.log('🔍 WikiCreate - Trying FormIDForWeb match:', form);
                  }
                  console.log('🔍 WikiCreate - Found form:', form);
                  
                  if (form) {
                    const name = form.name || form.Name || form.title || form.Title || form.FormName;
                    console.log('🔍 WikiCreate - Form name extracted:', name);
                    return name;
                  }
                }
              }
            }

            // Handle packet template links: {{patient_link_for_packet_ID}} (sibling of the form block above)
            if (decodedHref.includes('{{patient_link_for_packet_') && decodedHref.includes('}}')) {
              const match = decodedHref.match(/\{\{patient_link_for_packet_(.+?)\}\}/);
              console.log('🔍 WikiCreate - Packet template link detected (decoded):', decodedHref);
              if (match) {
                targetId = match[1];
                console.log('🔍 WikiCreate - Extracted packet ID:', targetId);
                if (targetId && this.packets && this.packets.length) {
                  const packet = this.packets.find(p => p.PacketID === targetId);
                  console.log('🔍 WikiCreate - Found packet:', packet);
                  if (packet) {
                    const name = packet.name || packet.Name || packet.title || packet.Title;
                    console.log('🔍 WikiCreate - Packet name extracted:', name);
                    return name;
                  }
                }
              }
            }

            return null;
          };
          
          // Handle link clicks to navigate to the real URL
          const handleLinkClick = (event) => {
            event.preventDefault();
            
            const link = event.currentTarget;
            const realHref = link.getAttribute('data-real-href');
            
            if (realHref) {
              console.log('Navigating to real URL:', realHref);
              
              // Handle different types of links
              if (realHref.includes('/app/wikiview/')) {
                // Navigate to wiki page
                window.location.href = realHref;
              } else if (realHref.includes('{{patient_link_for_form_')) {
                // Handle form template link
                console.log('Form template link clicked:', realHref);
              } else {
                // Fallback to regular navigation
                window.location.href = realHref;
              }
            }
          };
          
          links.forEach((link, index) => {
            // Skip if already processed
            if (link.getAttribute('data-real-href')) {
              return;
            }
            
            const readableName = findReadableNameFromHref(link.getAttribute('href'));

            if (readableName) {
              // Store the raw attribute value (not link.href which browser-resolves into a full URL with encoding)
              const realHref = link.getAttribute('href');
              
              link.setAttribute('data-real-href', realHref);
              link.setAttribute('data-link-name', readableName);
              link.setAttribute('data-link-type', realHref.includes('{{patient_link_for_form_') ? 'form' : 'wiki');

              const isFormTemplate = realHref.includes('{{patient_link_for_form_');
              link.setAttribute('title', isFormTemplate
                ? `Form Template: ${readableName} (works in patient emails/SMS)`
                : `Link to: ${readableName}`);
              
              // Override both href property and getAttribute method
              try {
                // Override the href property
                Object.defineProperty(link, 'href', {
                  get: function() {
                    return readableName;
                  },
                  set: function(value) {
                    // Ignore attempts to set href, keep our readable name
                  },
                  configurable: true,
                  enumerable: true
                });
                
                // Override getAttribute method to return readable name for 'href'
                const originalGetAttribute = link.getAttribute;
                link.getAttribute = function(name) {
                  if (name === 'href') {
                    return readableName;
                  }
                  return originalGetAttribute.call(this, name);
                };
                
                // Also try setting the href attribute to readable name (as backup)
                link.setAttribute('href', readableName);
                
              } catch (error) {
                console.error('Failed to override href property:', error);
                // Fallback: just set the attribute
                link.setAttribute('href', readableName);
              }
              
              // Add event listeners for better UX
              link.addEventListener('mouseenter', () => {
                // Could add hover effects here if needed
              });
              
              // Add click handler to navigate to real URL
              link.addEventListener('click', handleLinkClick);
            }
          });
          
        } catch (error) {
          console.error('Error enhancing existing links in WikiCreate:', error);
        }
      },

      // Clean enhanced links back to clean template syntax before saving
      cleanEnhancedLinks(editorData) {
        try {
          console.log('Starting enhanced link cleaning in WikiCreate...');

          editorData.blocks.forEach((block, blockIndex) => {
            if (block.type === 'paragraph' && block.data && block.data.text) {
              console.log(`Cleaning WikiCreate block ${blockIndex}:`, block.data.text);

              // Clean form template links: restore to {{patient_link_for_form_ID}} format
              let cleanedText = block.data.text;

              // CRITICAL: Strip localhost URLs - these should never be saved to database
              // Pattern: http://localhost:8080/app/wikiview/UUID -> /app/wikiview/UUID
              cleanedText = cleanedText.replace(/http:\/\/localhost:\d+\/app\/wikiview\//g, '/app/wikiview/');
              // Pattern: http://localhost:8080/anything -> remove the localhost prefix
              cleanedText = cleanedText.replace(/http:\/\/localhost:\d+\//g, '/');
              // Also handle https localhost (just in case)
              cleanedText = cleanedText.replace(/https:\/\/localhost:\d+\/app\/wikiview\//g, '/app/wikiview/');
              cleanedText = cleanedText.replace(/https:\/\/localhost:\d+\//g, '/');
              
              // Universal cleanup: Any link with data-real-href → restore href from the stored real URL
              // Handles clean values, URL-encoded values, and path-prefixed values from prior corrupted saves
              const enhancedLinkRegex = /<a[^>]*data-real-href="([^"]*)"[^>]*>([^<]*)<\/a>/g;
              cleanedText = cleanedText.replace(enhancedLinkRegex, (match, realHref, linkText) => {
                const decoded = decodeURIComponent(realHref);
                // Extract form template variable (strip any path prefix like /app/wikiedit/)
                const formVarMatch = decoded.match(/(\{\{patient_link_for_form_.+?\}\})/);
                if (formVarMatch) {
                  return `<a href="${formVarMatch[1]}">${linkText}</a>`;
                }
                // Extract wiki link path
                const wikiMatch = decoded.match(/(\/app\/wikiview\/[^\s"]+)/);
                if (wikiMatch) {
                  return `<a href="${wikiMatch[1]}">${linkText}</a>`;
                }
                // Fallback: use decoded realHref as-is
                return `<a href="${decoded}">${linkText}</a>`;
              });

              // Catch-all: Any <a> with form template or wiki href — strip extra attributes
              // regardless of attribute order (href may come before or after class)
              const formOrWikiLinkRegex = /<a\s[^>]*href="([^"]*(?:\{\{patient_link_for_form_[^"]*|\/app\/wikiview\/[^"]*))"[^>]*>([^<]*)<\/a>/g;
              cleanedText = cleanedText.replace(formOrWikiLinkRegex, (match, href, linkText) => {
                return `<a href="${href}">${linkText}</a>`;
              });
              
              if (cleanedText !== block.data.text) {
                console.log(`WikiCreate block ${blockIndex} cleaned:`, cleanedText);
                block.data.text = cleanedText;
              } else {
                console.log(`WikiCreate block ${blockIndex} no changes needed`);
              }
            }
          });
          
          console.log('WikiCreate enhanced link cleaning completed');
        } catch (error) {
          console.error('WikiCreate error cleaning enhanced links:', error);
        }
      },

      onReady() {
        console.log('WikiCreate Editor onReady callback fired');
        var elements = document.querySelectorAll('[contenteditable=false]');
        elements.forEach(element => {
          element.setAttribute('contenteditable' , true);
        });
        
        // Try to enhance links when editor is ready
        setTimeout(() => {
          this.enhanceExistingLinks();
        }, 1000);
      },

      async onChange(event) {
        // Debounce the preview update to avoid too many calls
        if (this.previewUpdateTimeout) {
          clearTimeout(this.previewUpdateTimeout);
        }
        
        this.previewUpdateTimeout = setTimeout(async () => {
          try {
            if (this.$refs.customEditor && this.$refs.customEditor.editor) {
              const savedData = await this.$refs.customEditor.editor.save();
              if (savedData && savedData.blocks) {
                this.liveEditorContent = savedData;
              }
            }
          } catch (e) {
            console.log('Preview update error:', e);
          }
        }, 300); // 300ms debounce
      },

      togglePreviewSection() {
        this.showInlinePreview = !this.showInlinePreview;
      },
    gotoCreatePage() {
        this.$router.push({
        name: 'WikiCreate',
        params: {
          pages : this.wikiPages
        }
      });
    }
  },  

  computed: {
    // The tags currently in the editor, so the variable gate judges what is about to be stored.
    selectedTagNames() {
      return (this.selectedTags || []).map(t => t && t.Tag).filter(Boolean);
    },

    isReviewRequestTemplate() {
      return isReviewRequestTagged(this.selectedTagNames);
    },

    ...mapState({
        loading: state => state.alert.loading,
        loaded: state => state.alert.loaded,
        errored: state => state.alert.errored,
        alertMessage: state => state.alert.message,
        dismissSuccessCountDown : state => state.alert.dismissSuccessCountDown,         
        siteID: state => state.customer.siteID,
        userID: state => state.customer.userID,
        sites: state => state.customer.sites,
        employees: state => state.customer.employees,
        settings: state => state.customer.settings,
    }),

    // allSettings comes from the global mixin on most pages (state.customer.settings
    // transformed via _.keyBy). WikiCreate does NOT use that mixin, so the same
    // derivation is done locally rather than pulling in the whole 28KB mixin for one
    // lookup. Keep this identical to mixin.js `allSettings()`.
    allSettings() {
      if(this.settings)
        return _.keyBy(this.settings, 'Property');
    },

    // Resolved locally from the already-mapped `sites` + `siteID` (this component
    // carries no mixin, so the store's getSite getter isn't available here).
    // NB: `state.customer.site` does not exist — reading it is what made this always
    // render the "Your Practice" placeholder. Fallback is kept for the window before
    // `sites` is bootstrapped.
    siteName() {
      const site = (this.sites || []).find(s => s.SiteID === this.siteID);
      return (site && site.Name) || 'Your Practice';
    },

    // Get site's verified email for email preview "From" field.
    // Same one-liner as WikiEdit.vue — both now delegate to the shared parser in
    // @/utils/verifiedSender.js rather than each carrying a copied comma-split.
    siteVerifiedEmail() {
      return pickVerifiedFromEmail(this.allSettings && this.allSettings.GENERAL_VERIFIED_EMAILS)
        || 'noreply@yourpractice.com';
    },

    currentTime() {
      const now = new Date();
      return now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    },

    englishSmsPreview() {
      // Use live editor content if available, otherwise fall back to initData
      let content = null;
      
      if (this.liveEditorContent && this.liveEditorContent.blocks) {
        // Real-time preview from editor
        content = this.liveEditorContent;
      } else if (this.initData && this.initData.blocks) {
        // Fallback to initial data
        content = this.initData;
      }
      
      if (!content || !content.blocks) return 'Start typing above...';

      try {
        // Shared FE/BE twin: headers are dropped as titles unless they're the only content.
        // Previously this was paragraphs-only, so a header-only template previewed as
        // "No text content yet" and its character counter vanished — while the backend
        // (post-2026-08-04 fix) does send that text. The preview must show what actually goes out.
        const rendered = extractSmsTextFromBlocks(content.blocks);
        return rendered ? rendered.replace(/\n/g, '<br>') : 'No text content yet';
      } catch (e) {
        return 'Error loading content';
      }
    },

    // SMS Character counting and segmentation
    smsPlainText() {
      // Get plain text from preview (strip HTML)
      const preview = this.englishSmsPreview;
      if (!preview || preview === 'Start typing above...' || preview === 'No text content yet') {
        return '';
      }
      // Strip HTML tags and decode entities
      const tmp = document.createElement('div');
      tmp.innerHTML = preview.replace(/<br\s*\/?>/gi, '\n');
      return tmp.textContent || tmp.innerText || '';
    },

    // Guidance severity ('ok' | 'warn' | 'block') for the SMS-template body, measured with sample
    // patient data (same as the status card). Drives hiding the phone mockup on any warn/block —
    // the status card is then the single signal (a preview of an undeliverable text would mislead).
    smsPreviewSeverity() {
      if (!this.isSmsTemplate) return 'ok';
      return getSmsGuidance(this.smsPlainText || '', { substituteTags: true }).severity;
    },

    smsIsUnicode() {
      // Check if text contains non-GSM-7 characters
      const text = this.smsPlainText;
      if (!text) return false;
      
      // GSM-7 compatible characters (basic + extended)
      const gsm7Pattern = /^[A-Za-z0-9 \n\r@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\|~\[\]€]*$/;
      return !gsm7Pattern.test(text);
    },

    smsCharCount() {
      return this.smsPlainText.length;
    },

    smsMaxCharsForOneSegment() {
      // Max chars for a single SMS (before splitting)
      return this.smsIsUnicode ? 70 : 160;
    },

    smsSegmentCount() {
      const len = this.smsCharCount;
      if (len === 0) return 1;
      
      if (this.smsIsUnicode) {
        // UCS-2 encoding: 70 chars single, 67 chars per segment when split
        if (len <= 70) return 1;
        return Math.ceil(len / 67);
      } else {
        // GSM-7 encoding: 160 chars single, 153 chars per segment when split
        if (len <= 160) return 1;
        return Math.ceil(len / 153);
      }
    },

    smsCounterClass() {
      if (this.smsSegmentCount >= 4) return 'counter-danger';
      if (this.smsSegmentCount >= 2) return 'counter-split';
      if (this.smsCharCount > (this.smsIsUnicode ? 50 : 140)) return 'counter-caution';
      return 'counter-ok';
    },

    // Split the message into segments for preview
    smsSplitPreview() {
      const text = this.smsPlainText;
      if (!text || this.smsSegmentCount <= 1) {
        return null; // No split needed
      }
      
      const segmentSize = this.smsIsUnicode ? 67 : 153;
      const segments = [];
      
      for (let i = 0; i < text.length; i += segmentSize) {
        segments.push(text.substring(i, i + segmentSize));
      }
      
      return segments;
    },

    // Email Preview computed properties
    emailSubjectPreview() {
      // Use custom email subject if set
      if (this.emailSubject && this.emailSubject.trim() !== '') {
        return this.emailSubject;
      }

      // Get header/title block for subject line
      let content = null;

      if (this.liveEditorContent && this.liveEditorContent.blocks) {
        content = this.liveEditorContent;
      } else if (this.initData && this.initData.blocks) {
        content = this.initData;
      }

      if (!content || !content.blocks) return 'Email Subject';
      
      // Find first header block for subject
      const headerBlock = content.blocks.find(b => b.type === 'header');
      if (headerBlock && headerBlock.data && headerBlock.data.text) {
        return this.stripHtmlForPreview(headerBlock.data.text);
      }
      
      // Fallback to first paragraph
      const firstPara = content.blocks.find(b => b.type === 'paragraph');
      if (firstPara && firstPara.data && firstPara.data.text) {
        const text = this.stripHtml(firstPara.data.text);
        return text.length > 60 ? text.substring(0, 60) + '...' : text;
      }
      
      return 'Email Subject';
    },

    emailBodyPreview() {
      let content = null;
      
      if (this.liveEditorContent && this.liveEditorContent.blocks) {
        content = this.liveEditorContent;
      } else if (this.initData && this.initData.blocks) {
        content = this.initData;
      }
      
      if (!content || !content.blocks) return '<p class="text-muted">Start typing above...</p>';
      
      // Build HTML from all blocks
      let html = '';
      content.blocks.forEach(block => {
        if (block.type === 'header' && block.data && block.data.text) {
          const level = block.data.level || 2;
          html += `<h${level} style="color: #333; margin-bottom: 10px;">${block.data.text}</h${level}>`;
        } else if (block.type === 'paragraph' && block.data && block.data.text) {
          html += `<p style="color: #555; line-height: 1.6; margin-bottom: 12px;">${block.data.text}</p>`;
        } else if (block.type === 'list' && block.data && block.data.items) {
          const tag = block.data.style === 'ordered' ? 'ol' : 'ul';
          html += `<${tag} style="color: #555; padding-left: 20px; margin-bottom: 12px;">`;
          block.data.items.forEach(item => {
            html += `<li style="margin-bottom: 4px;">${item}</li>`;
          });
          html += `</${tag}>`;
        }
      });
      
      return html || '<p class="text-muted">No content yet</p>';
    }
  }
};
</script>

<style src="./WikiCreate.scss" lang="scss" />
<style src="./WikiCreateEditor.scss" lang="scss" />

<style lang="scss" scoped>
/* Wiki Create Page - Modern Layout matching WikiView and WikiEdit */
.wiki-create-page {
  max-width: 100%;
}

/* Header Section */
.wiki-create-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;

  .header-left {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .back-link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #718096;
      text-decoration: none;
      font-size: 14px;
      transition: color 0.2s ease;

      &:hover {
        color: #3abf94;
      }

      .fa {
        font-size: 12px;
      }
    }

    .page-title {
      font-size: 24px;
      font-weight: 700;
      color: #2d3748;
      margin: 0;
    }
  }

  .header-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }
}

/* Content Wrapper - Two column layout */
.wiki-create-content-wrapper {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 24px;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 240px;
  }

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
}

/* Main Content Area */
.wiki-create-main {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
}

/* Sidebar */
.wiki-create-sidebar {
  @media (max-width: 991px) {
    order: -1;
  }

  .sidebar-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;

    .sidebar-title {
      font-size: 14px;
      font-weight: 600;
      color: #2d3748;
      margin: 0 0 16px 0;
      padding-bottom: 12px;
      border-bottom: 1px solid #e2e8f0;
    }

    .sidebar-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .sidebar-tag {
        display: inline-block;
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 500;
        background: #edf2f7;
        color: #4a5568;
        border-radius: 16px;
        text-decoration: none;
        transition: all 0.2s ease;

        &:hover {
          background: #3abf94;
          color: white;
        }
      }
    }
  }
}

/* Wiki Name Row - Full width above settings */
.wiki-name-row {
  display: flex;
}

.wiki-name-panel {
  flex: 1;
  border-color: #c3dafe;
  background: linear-gradient(135deg, #f0f4ff 0%, #fff 100%);

  .panel-header {
    border-bottom-color: #c3dafe;

    .panel-title {
      color: #3730a3;
    }
  }

  .required-star {
    color: #dc3545;
    font-weight: bold;
  }

  .wiki-name-input {
    font-size: 0.95rem;
    font-weight: 500;
  }
}

.wiki-name-error {
  border-color: #dc3545 !important;
  background: linear-gradient(135deg, #fff5f5 0%, #fff 100%) !important;

  .panel-header {
    border-bottom-color: #dc3545 !important;
  }
}

/* Settings Row - Flexbox container for side-by-side panels (matching WikiEdit) */
.settings-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* Base Settings Panel Styles */
.settings-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  flex: 1 1 180px;

  .panel-header {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    padding-bottom: 5px;
    border-bottom: 1px solid #eee;

    .panel-icon {
      font-size: 0.9rem;
      margin-right: 5px;
    }

    .panel-title {
      font-weight: 600;
      color: #333;
      font-size: 0.8rem;
    }
  }

  .panel-content {
    font-size: 0.85rem;
  }

  .compact-checkbox {
    margin-bottom: 2px !important;

    label {
      font-size: 0.8rem;
    }
  }
}

/* Tags Panel - Purple accent */
.tags-panel {
  border-color: #d6c9e8;
  background: linear-gradient(135deg, #f9f7fc 0%, #fff 100%);
  min-width: 200px;

  .panel-header {
    border-bottom-color: #d6c9e8;

    .panel-title {
      color: #6f42c1;
    }
  }

  .tags-content {
    .multiselect {
      font-size: 0.8rem;
      min-height: 32px;

      .multiselect__tags {
        min-height: 32px;
        padding: 4px 30px 0 6px;
        font-size: 0.8rem;
      }

      .multiselect__placeholder {
        font-size: 0.8rem;
        padding-top: 2px;
      }

      .multiselect__tag {
        font-size: 0.75rem;
        padding: 3px 20px 3px 6px;
        margin-bottom: 2px;
      }

      .multiselect__input {
        font-size: 0.8rem;
      }
    }
  }
}

/* Page Settings Panel - Blue accent */
.page-settings-panel {
  border-color: #bee5eb;
  background: linear-gradient(135deg, #f0f9ff 0%, #fff 100%);

  .panel-header {
    border-bottom-color: #bee5eb;

    .panel-title {
      color: #17a2b8;
    }
  }
}

/* Email Subject Panel - Blue accent */
.email-subject-panel {
  border-color: #b8daff;
  background: linear-gradient(135deg, #f0f7ff 0%, #fff 100%);
  min-width: 220px;

  .panel-header {
    border-bottom-color: #b8daff;

    .panel-title {
      color: #0056b3;
    }
  }

  .required-star {
    color: #dc3545;
    font-weight: bold;
  }

  .email-subject-input {
    font-size: 0.85rem;
  }
}

.email-subject-error {
  border-color: #dc3545 !important;
  background: linear-gradient(135deg, #fff5f5 0%, #fff 100%) !important;

  .panel-header {
    border-bottom-color: #dc3545 !important;
  }
}

/* Public Access Panel - Green accent */
.public-access-panel {
  border-color: #d4edda;
  background: linear-gradient(135deg, #f8fff9 0%, #fff 100%);

  .panel-header {
    border-bottom-color: #d4edda;

    .panel-title {
      color: #28a745;
    }
  }
}

/* Translation Panel - Cyan accent */
.translation-panel {
  border-color: #bee5eb;
  background: linear-gradient(135deg, #f0f9ff 0%, #fff 100%);

  .panel-header {
    border-bottom-color: #bee5eb;

    .panel-title {
      color: #17a2b8;
    }
  }
}

/* Variables Panel - Flexible width, amber accent */
.variables-panel {
  flex: 2 1 280px;
  border-color: #ffeeba;
  background: linear-gradient(135deg, #fffdf5 0%, #fff 100%);

  .panel-header {
    border-bottom-color: #ffeeba;

    .panel-title {
      color: #856404;
    }
  }

  .variables-content {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    .badge {
      font-size: 0.8rem;
      font-weight: 500;
      padding: 4px 8px;
      cursor: pointer;
    }
  }
}

/* Responsive - Stack on smaller screens */
@media (max-width: 768px) {
  .settings-row {
    flex-direction: column;
  }

  .settings-panel {
    flex: 1 1 100%;
  }
}

/* Spanish Translation Panel Styles */
.translation-settings-panel {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  
  .translation-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
    
    .translation-icon {
      font-size: 1.2rem;
      margin-right: 8px;
    }
    
    .translation-title {
      font-weight: 600;
      color: #333;
      font-size: 0.95rem;
    }
  }
  
  .translation-content {
    .translation-info {
      color: #666;
      margin-bottom: 0;
      line-height: 1.4;
      
      i {
        margin-right: 4px;
      }
    }
  }
}

/* Translation Language Row */
.translation-lang-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;

  .translation-checkbox {
    width: 15px;
    height: 15px;
    cursor: pointer;
    accent-color: #17a2b8;
  }

  .translation-label {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    margin-bottom: 0;
    flex: 1;
  }

  .lang-flag {
    font-size: 1rem;
  }

  .lang-name {
    font-size: 0.8rem;
    font-weight: 500;
    color: #333;
  }

  .status-badge {
    margin-left: auto;
    font-size: 0.75rem;
  }
}

/* Page Content Section */
.content-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.content-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 600;
  color: #2c3e50;
}

.content-section-icon {
  color: #667eea;
  font-size: 1rem;
}

.content-section-title {
  font-size: 0.95rem;
  font-weight: 600;
}

.content-section-hint {
  font-weight: 400;
  font-size: 13px;
  color: #94a3b8;
  margin-left: 12px;
}

/* Insert dropdown content */
.insert-dropdown-content {
  width: 520px;
  max-width: calc(100vw - 32px);
  max-height: 400px;
  overflow-y: auto;
}

.insert-section-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  color: #9ca3af;
  font-weight: 600;
  padding: 6px 12px 4px;
  margin: 0;
}

.variable-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.variable-chip {
  display: inline-block;
  padding: 3px 8px;
  font-size: 12px;
  color: #374151;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s;
  white-space: nowrap;
}

.variable-chip:hover {
  background-color: #e0f2fe;
  border-color: #93c5fd;
}

/* Emoji Insert Dropdown */
.emoji-inline-grid {
  max-height: 200px;
  overflow-y: auto;
}

.emoji-category-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  color: #9ca3af;
  margin: 8px 0 4px;
  font-weight: 600;
}

.emoji-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.emoji-item {
  cursor: pointer;
  font-size: 1.2rem;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1.4;
}

.emoji-item:hover {
  background-color: #f3f4f6;
}

.content-editor-container {
  padding: 16px;
  min-height: 300px;
}

/* ================================= */
/* INLINE SMS PREVIEW SECTION STYLES */
/* ================================= */
/* SMS status card — the single counter + warn/block guidance in a clean, high-contrast card,
   deliberately OFF the preview panel's purple gradient (readability). */
.sms-status-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
}

.sms-preview-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
  position: relative;
  z-index: 0;
}

.preview-header {
  cursor: pointer;
  user-select: none;
  padding: 16px 20px;
  background: rgba(255,255,255,0.1);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255,255,255,0.15);
  }
}

.preview-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.preview-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  
  i.fa-chevron-down,
  i.fa-chevron-right {
    font-size: 12px;
    opacity: 0.8;
  }
  
  .title-icon {
    font-size: 20px;
  }
}

.preview-status {
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    
    i {
      font-size: 11px;
    }
  }
  
  .status-pending {
    background: rgba(255,255,255,0.2);
    color: white;
  }
  
  .status-ready {
    background: #28a745;
    color: white;
  }
  
  .status-english-only {
    background: rgba(255,255,255,0.25);
    color: white;
  }
}

.inline-phone-preview {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 30px 20px;
  flex-wrap: wrap;
  
  &.single-phone {
    .phone-mockup-inline {
      transform: scale(1.05);
    }
  }
}

.phone-mockup-inline {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.phone-frame-inline {
  width: 220px;
  height: 380px;
  background: #1a1a1a;
  border-radius: 32px;
  padding: 10px;
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.4),
    inset 0 0 0 2px #333,
    inset 0 0 0 4px #1a1a1a;
  position: relative;
}

.phone-notch-inline {
  width: 90px;
  height: 20px;
  background: #1a1a1a;
  border-radius: 0 0 16px 16px;
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.phone-screen-inline {
  width: 100%;
  height: 100%;
  background: #f5f5f5;
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.phone-header-inline {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  padding: 28px 12px 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  font-weight: 500;
}

.phone-time-inline {
  font-weight: 600;
}

.phone-carrier-inline {
  font-size: 10px;
  opacity: 0.9;
}

.phone-content-inline {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  background: #e5ddd5;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4ccc4' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.message-bubble-inline {
  max-width: 90%;
  padding: 8px 12px;
  border-radius: 14px;
  margin-bottom: 6px;
  position: relative;
  word-wrap: break-word;
  
  &.outgoing {
    background: linear-gradient(135deg, #dcf8c6 0%, #c5e8a5 100%);
    margin-left: auto;
    border-bottom-right-radius: 4px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
  
  &.split-bubble {
    position: relative;
    margin-bottom: 10px;
    border: 2px dashed rgba(118, 75, 162, 0.3);
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%) !important;
  }
}

.split-indicator {
  position: absolute;
  top: -8px;
  right: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
}

.message-text-inline {
  font-size: 12px;
  line-height: 1.35;
  color: #303030;
}

.message-time-inline {
  font-size: 9px;
  color: #8a8a8a;
  text-align: right;
  margin-top: 3px;
}

.phone-label-inline {
  margin-top: 12px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.sms-counter {
  padding: 12px 20px;
  background: rgba(255,255,255,0.95);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 15px;
  font-size: 14px;
  
  &.counter-ok .char-count {
    color: #667eea;
  }
  
  &.counter-caution .char-count {
    color: #764ba2;
  }
  
  &.counter-split {
    background: linear-gradient(135deg, #f0e6ff 0%, #e6f0ff 100%);
    .char-count {
      color: #764ba2;
    }
  }
  
  &.counter-danger {
    background: #ffe6e6;
    .char-count {
      color: #dc3545;
    }
  }
}

.counter-main {
  display: flex;
  align-items: baseline;
  gap: 4px;
  
  .char-count {
    font-size: 22px;
    font-weight: 700;
  }
  
  .char-limit {
    color: #6c757d;
    font-size: 13px;
  }
}

.segment-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.segment-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  
  i {
    margin-right: 5px;
  }
  
  &.multi-segment {
    background: linear-gradient(135deg, #764ba2 0%, #9c27b0 100%);
    animation: pulse-glow 2s ease-in-out infinite;
  }
}

@keyframes pulse-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(118, 75, 162, 0.4); }
  50% { box-shadow: 0 0 10px 3px rgba(118, 75, 162, 0.3); }
}

.preview-footer {
  padding: 15px 20px;
  background: rgba(0,0,0,0.2);
  color: rgba(255,255,255,0.85);
  font-size: 13px;
  text-align: center;
  
  i {
    margin-right: 6px;
    opacity: 0.8;
  }
  
  code {
    background: rgba(255,255,255,0.2);
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    color: white;
  }
}

/* ================================= */
/* EMAIL PREVIEW SECTION STYLES      */
/* ================================= */
.email-preview-section {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(79, 172, 254, 0.3);
}

.email-preview-header {
  cursor: pointer;
  user-select: none;
  padding: 16px 20px;
  background: rgba(255,255,255,0.1);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255,255,255,0.15);
  }
}

.status-email {
  background: rgba(255,255,255,0.25);
  color: white;
}

.email-mockup {
  margin: 20px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0,0,0,0.15);
}

.email-client-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  padding: 12px 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #ddd;
}

.email-dots {
  display: flex;
  gap: 6px;
  
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    
    &.red { background: #ff5f56; }
    &.yellow { background: #ffbd2e; }
    &.green { background: #27ca40; }
  }
}

.email-tab {
  background: white;
  padding: 6px 14px;
  border-radius: 6px 6px 0 0;
  font-size: 12px;
  color: #666;
  border: 1px solid #ddd;
  border-bottom: none;
  margin-bottom: -1px;
  
  i {
    margin-right: 6px;
    color: #4facfe;
  }
}

.email-content {
  padding: 20px;
}

.email-field {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 13px;
  
  .field-label {
    font-weight: 600;
    color: #666;
    min-width: 60px;
  }
  
  .field-value {
    color: #333;
    flex: 1;
    
    &.subject {
      font-weight: 600;
      color: #222;
    }
  }
}

.email-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, #ddd, transparent);
  margin: 15px 0;
}

.email-body {
  min-height: 100px;
  padding: 10px 0;
  font-size: 14px;
  line-height: 1.6;
  
  h1, h2, h3, h4, h5, h6 {
    color: #333;
    margin-top: 0;
  }
  
  p {
    margin-bottom: 12px;
  }
  
  ul, ol {
    padding-left: 20px;
  }
}

.email-preview-footer {
  padding: 15px 20px;
  background: rgba(0,0,0,0.15);
  color: rgba(255,255,255,0.9);
  font-size: 13px;
  text-align: center;
  
  i {
    margin-right: 6px;
    opacity: 0.8;
  }
}

/* Responsive inline preview */
@media (max-width: 600px) {
  .inline-phone-preview {
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 15px;
  }
  
  .phone-frame-inline {
    width: 200px;
    height: 340px;
  }
  
  .preview-header-content {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
